import { z } from 'zod'
import './registry'
import { MoneySchema, type TenantId } from './common'
import { ShopSummarySchema, type ShopSummary } from './shop'

/**
 * Per-tenant WEBSHOP configuration (webshop plan §4), persisted as its own
 * `T#<tenantId>` / `WEBSHOPCONFIG` item — deliberately NOT on the `META` item:
 * the public `/shop/config` handler reads this item verbatim, and a dedicated
 * SK means a leak of unrelated tenant config (fulfilmentEmail, boxCapacity…)
 * is structurally impossible rather than prevented by strip-mode alone.
 *
 * Same two-variant pattern as schemas/settings.ts (read that file's class doc
 * for the full rationale):
 *   - `WebshopConfigSchema` — OpenAPI-registered wire contract, every field
 *     `.default()`ed so a missing key resolves.
 *   - `WebshopConfigReadSchema` — every field additionally `.catch()`ed to its
 *     default; used at raw-DDB read sites only, so one corrupt value never
 *     resets its neighbours.
 * The parity test in test/schemas/webshop-config.test.ts pins both views (and
 * DEFAULT_WEBSHOP_CONFIG) to WEBSHOP_CONFIG_DEFAULTS.
 *
 * An unconfigured tenant parses to the defaults below (empty identity
 * strings). That is fine: a storefront only goes live once a `DOMAIN#<host>`
 * mapping exists, and creating that mapping is the onboarding step that also
 * fills this config in.
 *
 * Shipping is a MATRIX (D3): zones — each a set of countries — with a price
 * per record-count tier and an optional free-above threshold. The default
 * matrix is a single NL zone at today's flat 4.95 / free ≥ 75.00, so an
 * unconfigured tenant behaves exactly as the placeholder did; the client's
 * real zones and rates are a config edit (the `shippingZones` block of the
 * store-config file, `make seed-webshop-config`), not a code change. Zone ORDER matters: the first zone is
 * the shop's home zone — what the storefront quotes before a country is
 * chosen, and what the announcement bar's "free from" reads.
 */
const WEBSHOP_CONFIG_DEFAULTS = {
  shopName: '',
  shopSlug: '',
  /** Canonical customer-facing storefront origin, e.g. `https://shop.toon-luisterbar.nl`. */
  storefrontUrl: '',
  contactEmail: '',
  contactPhone: '',
  kvkNumber: '',
  btwId: '',
  /** Display address for the footer/contact page (plan §10.4), as shown text. */
  address: '',
  pickupEnabled: false,
  pickupAddress: '',
  openingHours: '',
  orderNumberPrefix: '',
  aggregatorOptIn: false,
  /** Display name transactional email is sent under (Phase 3+). */
  senderName: '',
  /**
   * Payment methods offered at checkout (D2 — recommendation pending the
   * client's confirmation). INSTANT settlement only, and that is a structural
   * requirement rather than a preference: checkout holds a 1-of-1 record for
   * ~30 minutes, so a method that settles in days (bank transfer) would either
   * strand stock or break the hold model. Adding another instant Mollie method
   * later is a config change, nothing more.
   */
  paymentMethods: ['ideal', 'creditcard', 'bancontact'],
} as const

/** Lowercase URL slug; empty until the shop is configured. */
const SlugValueSchema = z.string().regex(/^[a-z0-9-]*$/, 'lowercase url slug')

// ── Shipping matrix (D3) ──────────────────────────────────────────────

/**
 * One price step of a zone, keyed on how many records the parcel holds —
 * the client prices by count ("one record", "up to three", "more"), which
 * for LPs tracks weight closely enough that a weight table would only add
 * a second number to keep in sync with the first.
 */
export const ShippingTierSchema = z
  .object({
    /**
     * The tier applies while the record count is ≤ this. `null` = no upper
     * bound, allowed only as the LAST tier (validated on the zone). A zone
     * whose last tier is BOUNDED carries the carrier's PER-PARCEL maximum
     * (e.g. PostNL: ~121 records inside the EU, ~68 beyond): an order above
     * it is quoted as the cheapest valid split into several parcels
     * (lib/shipping.ts quoteShipping), shown to the customer as "ships as N
     * packages" — never the top tier's price for a parcel no carrier takes,
     * and never a refusal.
     */
    upToItems: z.number().int().min(1).nullable(),
    /** Price for the whole parcel in this tier, incl. VAT. */
    rate: MoneySchema,
  })
  .openapi('ShippingTier')
export type ShippingTier = z.infer<typeof ShippingTierSchema>

/** ISO 3166-1 alpha-2, upper-case — the value the checkout country select carries. */
const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2 country code')

/**
 * A set of countries that ship at the same tariff. Countries are explicit —
 * no wildcard "rest of world" — because a country outside every zone is a
 * country the shop does not ship to, and that must be a hard 400 at checkout
 * (ERR_SHIPPING_COUNTRY_UNSUPPORTED), never a silent default tariff.
 */
export const ShippingZoneSchema = z
  .object({
    /** Stable handle for edits and logs: `nl`, `be-de`, `eu`. */
    id: z
      .string()
      .regex(/^[a-z0-9-]+$/, 'lowercase id')
      .max(30),
    countries: z.array(CountryCodeSchema).min(1).max(60),
    /** Ascending by `upToItems`; at most ONE open-ended tier, only in last place.
     *  A bounded last tier = the zone's per-parcel maximum (see ShippingTierSchema). */
    tiers: z.array(ShippingTierSchema).min(1).max(10),
    /**
     * Records subtotal at/above which shipping is free in this zone; null =
     * never. `"0.00"` is REFUSED (see the superRefine): under the retired
     * `freeShippingThreshold` field it meant "disabled", but on a zone it
     * would mean free shipping on every order — an operator porting the old
     * idiom must hear about the inversion at seed/PATCH time, not on receipts.
     */
    freeAbove: MoneySchema.nullable(),
    /** Display-only carrier name for the tariff page; null hides it. */
    carrier: z.string().max(40).nullable(),
  })
  .superRefine((zone, ctx) => {
    if (new Set(zone.countries).size !== zone.countries.length) {
      ctx.addIssue({ code: 'custom', message: 'duplicate country in zone', path: ['countries'] })
    }
    if (zone.freeAbove === '0.00') {
      ctx.addIssue({
        code: 'custom',
        message:
          'freeAbove "0.00" would make every order ship free — use null for a zone without free shipping',
        path: ['freeAbove'],
      })
    }
    zone.tiers.forEach((tier, i) => {
      const last = i === zone.tiers.length - 1
      if (!last && tier.upToItems === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'only the last tier may be open-ended',
          path: ['tiers', i, 'upToItems'],
        })
      }
      const prev = i > 0 ? zone.tiers[i - 1]!.upToItems : null
      if (i > 0 && tier.upToItems !== null && prev !== null && tier.upToItems <= prev) {
        ctx.addIssue({
          code: 'custom',
          message: 'tiers must be strictly ascending by upToItems',
          path: ['tiers', i, 'upToItems'],
        })
      }
    })
  })
  .openapi('ShippingZone')
export type ShippingZone = z.infer<typeof ShippingZoneSchema>

/**
 * The whole matrix. Cross-zone rules live here: ids unique, and a country in
 * at most ONE zone — two zones claiming `BE` would make the quote depend on
 * array order, which is exactly the ambiguity a tariff table must not have.
 */
export const ShippingMatrixSchema = z
  .array(ShippingZoneSchema)
  .min(1)
  .max(20)
  .superRefine((zones, ctx) => {
    const ids = new Set<string>()
    const seen = new Map<string, string>()
    zones.forEach((zone, i) => {
      if (ids.has(zone.id)) {
        ctx.addIssue({ code: 'custom', message: `duplicate zone id "${zone.id}"`, path: [i, 'id'] })
      }
      ids.add(zone.id)
      for (const c of zone.countries) {
        const other = seen.get(c)
        if (other !== undefined) {
          ctx.addIssue({
            code: 'custom',
            message: `country ${c} is in both "${other}" and "${zone.id}"`,
            path: [i, 'countries'],
          })
        }
        seen.set(c, zone.id)
      }
    })
  })
  .openapi('ShippingMatrix')

/**
 * The matrix an unconfigured tenant runs on — one NL zone at the flat
 * placeholder tariff, so nothing changes until the client's real zones are
 * seeded. A function, not a constant: every parse and every default must get
 * its own array (see the "own array" tests for paymentMethods).
 */
export function defaultShippingZones(): ShippingZone[] {
  return [
    {
      id: 'nl',
      countries: ['NL'],
      tiers: [{ upToItems: null, rate: '4.95' }],
      freeAbove: '75.00',
      carrier: null,
    },
  ]
}

/**
 * Payment methods the storefront may offer, named as the PSP names them
 * (Mollie method ids). Deliberately an enum, not free text: an unknown value
 * would be silently dropped by the PSP at checkout-creation time, showing the
 * customer a payment screen missing the method the shop thought it enabled.
 * Bank transfer is absent BY DESIGN — see the defaults' doc for why.
 */
export const WebshopPaymentMethodSchema = z
  .enum(['ideal', 'creditcard', 'bancontact', 'paypal', 'applepay'])
  .openapi('WebshopPaymentMethod')
export type WebshopPaymentMethod = z.infer<typeof WebshopPaymentMethodSchema>

/** Email or empty-string-while-unconfigured. */
const EmailValueSchema = z.union([z.string().email(), z.literal('')])

/**
 * An https origin with no path and no trailing slash (so link building is a
 * plain concatenation), or empty while unconfigured.
 */
const StorefrontUrlSchema = z.union([
  z.string().regex(/^https:\/\/[a-z0-9.-]+(:\d+)?$/, 'https origin without trailing slash'),
  z.literal(''),
])

export const WebshopConfigSchema = z
  .object({
    /** Public shop display name — becomes `ShopSummary.name` on every record. */
    shopName: z.string().max(120).default(WEBSHOP_CONFIG_DEFAULTS.shopName),
    /** Public shop slug — becomes `ShopSummary.slug` (aggregator profile URL). */
    shopSlug: SlugValueSchema.max(60).default(WEBSHOP_CONFIG_DEFAULTS.shopSlug),
    /**
     * The shop's canonical storefront ORIGIN (`https://shop.…`, no trailing
     * slash) — where a customer's browser goes.
     *
     * Load-bearing for magic links (plan §7): every order email builds
     * `<storefrontUrl>/bestelling/<id>?t=…` from THIS, not from the host the
     * request arrived on. Those differ: the API resolves tenants from the
     * edge-stamped `x-forwarded-host`, which on the deployed topology is the
     * API's own CloudFront domain — a link built from it 404s, and the
     * confirmation mail is a legal duty (plan §10.7). Emails skip rather than
     * send a broken link when this is empty.
     */
    storefrontUrl: StorefrontUrlSchema.default(WEBSHOP_CONFIG_DEFAULTS.storefrontUrl),
    /** Customer-reachable email — legally required on the storefront (plan §10.4). */
    contactEmail: EmailValueSchema.default(WEBSHOP_CONFIG_DEFAULTS.contactEmail),
    contactPhone: z.string().max(40).default(WEBSHOP_CONFIG_DEFAULTS.contactPhone),
    /** KvK registration number — legally required in the footer (plan §10.4). */
    kvkNumber: z.string().max(20).default(WEBSHOP_CONFIG_DEFAULTS.kvkNumber),
    /** BTW-identificatienummer — legally required in the footer (plan §10.4). */
    btwId: z.string().max(30).default(WEBSHOP_CONFIG_DEFAULTS.btwId),
    /** Geographical address, display text (multi-line allowed). */
    address: z.string().max(300).default(WEBSHOP_CONFIG_DEFAULTS.address),
    /** Whether click & collect is offered at checkout (Phase 3). */
    pickupEnabled: z.boolean().default(WEBSHOP_CONFIG_DEFAULTS.pickupEnabled),
    /** Pickup address shown at the click & collect choice (multi-line allowed: `\n`). */
    pickupAddress: z.string().max(300).default(WEBSHOP_CONFIG_DEFAULTS.pickupAddress),
    /**
     * Opening hours, display text. Free format so each shop decides what to
     * show (per day, or ranges like "di–vr 10:00–18:00"); `\n` separates the
     * lines and the storefront renders them with `white-space: pre-line` —
     * plain text end to end, no HTML to sanitise. Shown in the footer and in
     * every pickup information box.
     */
    openingHours: z.string().max(500).default(WEBSHOP_CONFIG_DEFAULTS.openingHours),
    /**
     * Prefix of the human-facing order number (`RV` in `RV-2026-0834`, see
     * lib/order-number.ts). Empty = derive the shop name's initials at
     * allocation time.
     */
    orderNumberPrefix: z
      .string()
      .regex(/^[A-Z0-9]{0,6}$/, 'uppercase letters/digits, max 6')
      .default(WEBSHOP_CONFIG_DEFAULTS.orderNumberPrefix),
    /** The shipping matrix (D3) — zones × record-count tiers; see class doc. */
    // A FACTORY default: zod 4 hands a value default back by reference, so a
    // caller mutating one parse's matrix would be editing every later parse's.
    shippingZones: ShippingMatrixSchema.default(() => defaultShippingZones()),
    /** Opt-in to the randomvinyl.nl aggregator (Phase 6) — dormant until then. */
    aggregatorOptIn: z.boolean().default(WEBSHOP_CONFIG_DEFAULTS.aggregatorOptIn),
    /** Sender display name for transactional email (v1 sends from the platform domain). */
    senderName: z.string().max(120).default(WEBSHOP_CONFIG_DEFAULTS.senderName),
    /** Payment methods offered at checkout (D2) — see the defaults' doc. */
    paymentMethods: z
      .array(WebshopPaymentMethodSchema)
      .default([...WEBSHOP_CONFIG_DEFAULTS.paymentMethods]),
  })
  .strip()
  .openapi('WebshopConfig')

/**
 * Read-tolerant variant for parsing the raw DynamoDB item — same shape, each
 * field `.catch()`ed to its default. Not OpenAPI-registered (zod-to-openapi
 * cannot introspect ZodCatch; read tolerance is not part of the wire contract).
 */
export const WebshopConfigReadSchema = z
  .object({
    shopName: WebshopConfigSchema.shape.shopName.catch(WEBSHOP_CONFIG_DEFAULTS.shopName),
    shopSlug: WebshopConfigSchema.shape.shopSlug.catch(WEBSHOP_CONFIG_DEFAULTS.shopSlug),
    storefrontUrl: WebshopConfigSchema.shape.storefrontUrl.catch(
      WEBSHOP_CONFIG_DEFAULTS.storefrontUrl
    ),
    contactEmail: WebshopConfigSchema.shape.contactEmail.catch(
      WEBSHOP_CONFIG_DEFAULTS.contactEmail
    ),
    contactPhone: WebshopConfigSchema.shape.contactPhone.catch(
      WEBSHOP_CONFIG_DEFAULTS.contactPhone
    ),
    kvkNumber: WebshopConfigSchema.shape.kvkNumber.catch(WEBSHOP_CONFIG_DEFAULTS.kvkNumber),
    btwId: WebshopConfigSchema.shape.btwId.catch(WEBSHOP_CONFIG_DEFAULTS.btwId),
    address: WebshopConfigSchema.shape.address.catch(WEBSHOP_CONFIG_DEFAULTS.address),
    pickupEnabled: WebshopConfigSchema.shape.pickupEnabled.catch(
      WEBSHOP_CONFIG_DEFAULTS.pickupEnabled
    ),
    pickupAddress: WebshopConfigSchema.shape.pickupAddress.catch(
      WEBSHOP_CONFIG_DEFAULTS.pickupAddress
    ),
    openingHours: WebshopConfigSchema.shape.openingHours.catch(
      WEBSHOP_CONFIG_DEFAULTS.openingHours
    ),
    orderNumberPrefix: WebshopConfigSchema.shape.orderNumberPrefix.catch(
      WEBSHOP_CONFIG_DEFAULTS.orderNumberPrefix
    ),
    // A corrupt matrix falls back to the DEFAULT matrix, never to "no
    // shipping": an empty zone list would refuse every shipped order.
    shippingZones: WebshopConfigSchema.shape.shippingZones.catch(() => defaultShippingZones()),
    aggregatorOptIn: WebshopConfigSchema.shape.aggregatorOptIn.catch(
      WEBSHOP_CONFIG_DEFAULTS.aggregatorOptIn
    ),
    senderName: WebshopConfigSchema.shape.senderName.catch(WEBSHOP_CONFIG_DEFAULTS.senderName),
    paymentMethods: WebshopConfigSchema.shape.paymentMethods.catch([
      ...WEBSHOP_CONFIG_DEFAULTS.paymentMethods,
    ]),
  })
  .strip()

/**
 * PATCH /webshop/settings body — partial update, admin-only (enforced in the
 * handler). Built field-by-field, not via `.partial()`, for the same
 * default-suppression reason as UpdateTenantSettingsRequestSchema.
 */
export const UpdateWebshopConfigRequestSchema = z
  .object({
    shopName: z.string().max(120).optional(),
    shopSlug: SlugValueSchema.max(60).optional(),
    storefrontUrl: StorefrontUrlSchema.optional(),
    contactEmail: EmailValueSchema.optional(),
    contactPhone: z.string().max(40).optional(),
    kvkNumber: z.string().max(20).optional(),
    btwId: z.string().max(30).optional(),
    address: z.string().max(300).optional(),
    pickupEnabled: z.boolean().optional(),
    pickupAddress: z.string().max(300).optional(),
    openingHours: z.string().max(500).optional(),
    orderNumberPrefix: z
      .string()
      .regex(/^[A-Z0-9]{0,6}$/, 'uppercase letters/digits, max 6')
      .optional(),
    shippingZones: ShippingMatrixSchema.optional(),
    aggregatorOptIn: z.boolean().optional(),
    senderName: z.string().max(120).optional(),
    paymentMethods: z.array(WebshopPaymentMethodSchema).min(1).optional(),
  })
  .strict()
  .openapi('UpdateWebshopConfigRequest')

/** Defaults for a tenant with no WEBSHOPCONFIG item yet. */
export const DEFAULT_WEBSHOP_CONFIG: WebshopConfig = {
  ...WEBSHOP_CONFIG_DEFAULTS,
  shippingZones: defaultShippingZones(),
  paymentMethods: [...WEBSHOP_CONFIG_DEFAULTS.paymentMethods],
}

export type WebshopConfig = z.infer<typeof WebshopConfigSchema>
export type UpdateWebshopConfigRequest = z.infer<typeof UpdateWebshopConfigRequestSchema>

/**
 * What the anonymous `GET /shop/config` returns — a PROJECTION of the config,
 * not the config itself: staff-only knobs (aggregatorOptIn, senderName) stay
 * server-side; identity fields are regrouped the way the storefront consumes
 * them. `shipping` is the tenant's whole matrix — public by nature (it IS the
 * tariff page), and the storefront quotes from it with the same rule the
 * server charges by (lib/shipping.ts and its storefront twin).
 */
export const ShopConfigSchema = z
  .object({
    shop: ShopSummarySchema,
    contactEmail: z.string(),
    phone: z.string(),
    kvk: z.string(),
    btwId: z.string(),
    address: z.string(),
    pickup: z.object({ enabled: z.boolean(), address: z.string() }),
    /** Opening hours, multiline display text (`\n` line breaks, render pre-line). */
    openingHours: z.string(),
    /** The shipping matrix, first zone = home zone (see WebshopConfig doc). */
    shipping: z.array(ShippingZoneSchema),
    /** Payment methods the checkout may offer (D2) — what the PSP will accept. */
    paymentMethods: z.array(WebshopPaymentMethodSchema),
  })
  .openapi('ShopConfig')

export type ShopConfig = z.infer<typeof ShopConfigSchema>

/** The one place `WebshopConfig` becomes the public shape (an explicit pick). */
export function toShopConfig(tenantId: TenantId, config: WebshopConfig): ShopConfig {
  const shop: ShopSummary = { id: tenantId, name: config.shopName, slug: config.shopSlug }
  return {
    shop,
    contactEmail: config.contactEmail,
    phone: config.contactPhone,
    kvk: config.kvkNumber,
    btwId: config.btwId,
    address: config.address,
    pickup: { enabled: config.pickupEnabled, address: config.pickupAddress },
    openingHours: config.openingHours,
    shipping: config.shippingZones,
    paymentMethods: config.paymentMethods,
  }
}

// The item's sort key lives in the import-free ./webshop-keys (lib/ddb.ts
// imports it there); re-exported so the barrel keeps offering it.
export { WEBSHOP_CONFIG_SK } from './webshop-keys'
